import { useFormAction, useNavigation } from '@remix-run/react'
import { clsx, type ClassValue } from 'clsx'
import { format as dateFormat } from 'date-fns'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useSpinDelay } from 'spin-delay'
import { extendTailwindMerge } from 'tailwind-merge'
import { extendedTheme } from './extended-theme.ts'
export function getUserImgSrc(imageId?: string | null) {
	return imageId ? `/resources/user-images/${imageId}` : '/img/user.png'
}

/**
 * Treat a stored "calendar day" `DateTime` (e.g. macrocycle.startDate,
 * phase.startDate) as a wall-clock date. Date inputs and `new Date(YYYY-
 * MM-DD)` parse to UTC midnight, but `format()` and recharts read in the
 * browser's local timezone — so any user west of UTC sees the date as
 * the previous day. This wraps the stored UTC YYYY-MM-DD back into a
 * local-midnight Date so display matches the picker.
 *
 * Use this anywhere a calendar-day field gets formatted, passed to a
 * chart axis, or compared against another wall-date — not for true
 * timestamps (e.g. `createdAt`) where the time component is meaningful.
 */
export function asWallDate(d: Date | string | null | undefined): Date {
	const date = d instanceof Date ? d : new Date(d ?? 0)
	return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
}

/**
 * Inverse companion to `asWallDate`: normalize a Date to UTC midnight of its
 * UTC calendar day — the storage convention for calendar-day fields
 * (`Workout.date`, backdated `completedAt`).
 */
export function utcDayStart(d: Date): Date {
	return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()))
}

/**
 * Re-express an instant as the wall clock a viewer in `timeZone` would read,
 * returned as a Date whose *local* (process-timezone) fields carry that wall
 * clock — so `toDateString()`, `getDate()` and friends answer "what calendar
 * day is it for THAT user", not "for this server".
 *
 * This is the server-side companion to `asWallDate`: `asWallDate` reinterprets
 * a stored UTC calendar-day as a local one, while this reinterprets a true
 * timestamp into a named zone. Use it wherever a loader has to decide "is this
 * today?" about a real instant — the process timezone is a deployment detail
 * (Fly containers run UTC) and must never decide a user's calendar day.
 *
 * The user's zone comes from the `@epic-web/client-hints` time-zone hint
 * (`getHints(request).timeZone`), which the root loader already collects.
 *
 * Two deliberate limits:
 *  - the returned Date is NOT the same instant; it is only meaningful for
 *    reading calendar fields, never for arithmetic against un-shifted dates.
 *  - `Intl` throws `RangeError` on an unknown zone (the hint arrives in a
 *    user-writable cookie), so an unusable zone falls back to the input
 *    unchanged rather than breaking the page.
 */
export function inTimeZone(d: Date, timeZone: string | null | undefined): Date {
	if (!timeZone) return d
	try {
		const parts = new Intl.DateTimeFormat('en-US', {
			timeZone,
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
			hour: '2-digit',
			minute: '2-digit',
			second: '2-digit',
			hour12: false,
		}).formatToParts(d)
		const at = (type: string) =>
			Number(parts.find((p) => p.type === type)?.value)
		const year = at('year')
		if (!Number.isFinite(year)) return d
		return new Date(
			year,
			at('month') - 1,
			at('day'),
			// Some ICU builds render midnight as hour 24 under hour12:false.
			at('hour') % 24,
			at('minute'),
			at('second'),
			d.getMilliseconds(),
		)
	} catch {
		return d
	}
}

export function getWorkoutImgSrc(imageId?: string | null) {
	return imageId ? `/resources/workout-images/${imageId}` : '/img/workout.png'
}

export function getGroupImgSrc(imageId?: string | null) {
	return imageId ? `/resources/group-images/${imageId}` : '/img/group.png'
}

export function getExerciseImgSrc(imageId?: string | null) {
	return imageId ? `/resources/exercise-images/${imageId}` : '/img/exercise.png'
}

export function getAchievementTypeImgSrc(imageId: string | null) {
	if (!imageId) return ''
	return `/resources/achievement-type-image/${imageId}`
}

export function getErrorMessage(error: unknown) {
	if (typeof error === 'string') return error
	if (
		error &&
		typeof error === 'object' &&
		'message' in error &&
		typeof error.message === 'string'
	) {
		return error.message
	}
	console.error('Unable to get error message for error', error)
	return 'Unknown Error'
}

function formatColors() {
	const colors = []
	for (const [key, color] of Object.entries(extendedTheme.colors)) {
		if (typeof color === 'string') {
			colors.push(key)
		} else {
			const colorGroup = Object.keys(color).map((subKey) =>
				subKey === 'DEFAULT' ? '' : subKey,
			)
			colors.push({ [key]: colorGroup })
		}
	}
	return colors
}

const customTwMerge = extendTailwindMerge<string, string>({
	extend: {
		theme: {
			colors: formatColors(),
			borderRadius: Object.keys(extendedTheme.borderRadius),
		},
		classGroups: {
			'font-size': [
				{
					text: Object.keys(extendedTheme.fontSize),
				},
			],
		},
	},
})

export function cn(...inputs: ClassValue[]) {
	return customTwMerge(clsx(inputs))
}

export function getDomainUrl(request: Request) {
	const host =
		request.headers.get('X-Forwarded-Host') ??
		request.headers.get('host') ??
		new URL(request.url).host
	const protocol = request.headers.get('X-Forwarded-Proto') ?? 'http'
	return `${protocol}://${host}`
}

export function getReferrerRoute(request: Request) {
	const referrer =
		request.headers.get('referer') ??
		request.headers.get('referrer') ??
		request.referrer
	if (referrer) {
		try {
			const refUrl = new URL(referrer)
			const reqUrl = new URL(request.url)
			if (refUrl.host === reqUrl.host) {
				return refUrl.pathname + refUrl.search
			}
		} catch {
			// ignore
		}
	}
	return '/'
}

/**
 * Merge multiple headers objects into one (uses set so headers are overridden)
 */
export function mergeHeaders(
	...headers: Array<ResponseInit['headers'] | null | undefined>
) {
	const merged = new Headers()
	for (const header of headers) {
		if (!header) continue
		for (const [key, value] of new Headers(header).entries()) {
			merged.set(key, value)
		}
	}
	return merged
}

/**
 * Combine multiple header objects into one (uses append so headers are not overridden)
 */
export function combineHeaders(
	...headers: Array<ResponseInit['headers'] | null | undefined>
) {
	const combined = new Headers()
	for (const header of headers) {
		if (!header) continue
		for (const [key, value] of new Headers(header).entries()) {
			combined.append(key, value)
		}
	}
	return combined
}

/**
 * Combine multiple response init objects into one (uses combineHeaders)
 */
export function combineResponseInits(
	...responseInits: Array<ResponseInit | null | undefined>
) {
	let combined: ResponseInit = {}
	for (const responseInit of responseInits) {
		combined = {
			...responseInit,
			headers: combineHeaders(combined.headers, responseInit?.headers),
		}
	}
	return combined
}

/**
 * Returns true if the current navigation is submitting the current route's
 * form. Defaults to the current route's form action and method POST.
 *
 * Defaults state to 'non-idle'
 *
 * NOTE: the default formAction will include query params, but the
 * navigation.formAction will not, so don't use the default formAction if you
 * want to know if a form is submitting without specific query params.
 */
export function useIsPending({
	formAction,
	formMethod = 'POST',
	state = 'non-idle',
}: {
	formAction?: string
	formMethod?: 'POST' | 'GET' | 'PUT' | 'PATCH' | 'DELETE'
	state?: 'submitting' | 'loading' | 'non-idle'
} = {}) {
	const contextualFormAction = useFormAction()
	const navigation = useNavigation()
	const isPendingState =
		state === 'non-idle'
			? navigation.state !== 'idle'
			: navigation.state === state
	return (
		isPendingState &&
		navigation.formAction === (formAction ?? contextualFormAction) &&
		navigation.formMethod === formMethod
	)
}

/**
 * This combines useSpinDelay (from https://npm.im/spin-delay) and useIsPending
 * from our own utilities to give you a nice way to show a loading spinner for
 * a minimum amount of time, even if the request finishes right after the delay.
 *
 * This avoids a flash of loading state regardless of how fast or slow the
 * request is.
 */
export function useDelayedIsPending({
	formAction,
	formMethod,
	delay = 400,
	minDuration = 300,
}: Parameters<typeof useIsPending>[0] &
	Parameters<typeof useSpinDelay>[1] = {}) {
	const isPending = useIsPending({ formAction, formMethod })
	const delayedIsPending = useSpinDelay(isPending, {
		delay,
		minDuration,
	})
	return delayedIsPending
}

function callAll<Args extends Array<unknown>>(
	...fns: Array<((...args: Args) => unknown) | undefined>
) {
	return (...args: Args) => fns.forEach((fn) => fn?.(...args))
}

/**
 * Use this hook with a button and it will make it so the first click sets a
 * `doubleCheck` state to true, and the second click will actually trigger the
 * `onClick` handler. This allows you to have a button that can be like a
 * "are you sure?" experience for the user before doing destructive operations.
 */
export function useDoubleCheck() {
	const [doubleCheck, setDoubleCheck] = useState(false)

	function getButtonProps(
		props?: React.ButtonHTMLAttributes<HTMLButtonElement>,
	) {
		const onBlur: React.ButtonHTMLAttributes<HTMLButtonElement>['onBlur'] =
			() => setDoubleCheck(false)

		const onClick: React.ButtonHTMLAttributes<HTMLButtonElement>['onClick'] =
			doubleCheck
				? undefined
				: (e) => {
						e.preventDefault()
						setDoubleCheck(true)
					}

		const onKeyUp: React.ButtonHTMLAttributes<HTMLButtonElement>['onKeyUp'] = (
			e,
		) => {
			if (e.key === 'Escape') {
				setDoubleCheck(false)
			}
		}

		return {
			...props,
			onBlur: callAll(onBlur, props?.onBlur),
			onClick: callAll(onClick, props?.onClick),
			onKeyUp: callAll(onKeyUp, props?.onKeyUp),
		}
	}

	return { doubleCheck, getButtonProps }
}

/**
 * Simple debounce implementation
 */
export function debounce<
	Callback extends (...args: Parameters<Callback>) => void,
>(fn: Callback, delay: number) {
	let timer: ReturnType<typeof setTimeout> | null = null
	return (...args: Parameters<Callback>) => {
		if (timer) clearTimeout(timer)
		timer = setTimeout(() => {
			fn(...args)
		}, delay)
	}
}

/**
 * Debounce a callback function
 */
export function useDebounce<
	Callback extends (...args: Parameters<Callback>) => ReturnType<Callback>,
>(callback: Callback, delay: number) {
	const callbackRef = useRef(callback)
	useEffect(() => {
		callbackRef.current = callback
	})
	return useMemo(
		() =>
			debounce(
				(...args: Parameters<Callback>) => callbackRef.current(...args),
				delay,
			),
		[delay],
	)
}

export async function downloadFile(url: string, retries: number = 0) {
	const MAX_RETRIES = 3
	try {
		const response = await fetch(url)
		if (!response.ok) {
			throw new Error(`Failed to fetch image with status ${response.status}`)
		}
		const contentType = response.headers.get('content-type') ?? 'image/jpg'
		const blob = Buffer.from(await response.arrayBuffer())
		return { contentType, blob }
	} catch (e) {
		if (retries > MAX_RETRIES) throw e
		return downloadFile(url, retries + 1)
	}
}

/**
 * Convert an ISO string to a Date object
 * Returns undefined if conversion fails or if the input is falsy
 * @param iso string
 * @returns Date | undefined
 */
export const dateFromIso = (
	iso?: string | Date | null,
	fallback?: Date | undefined,
) => {
	if (!iso) return fallback
	try {
		const date = new Date(iso)
		return isNaN(date.getTime()) ? fallback : date
	} catch {
		return fallback
	}
}

export const dateFromIsoOrNow = (iso?: string | Date | null): Date => {
	return dateFromIso(iso, new Date()) ?? new Date()
}

export const formatDate = (date: Date, format: string) => {
	return dateFormat(date, format)
}

export const formatDateFromIso = (format: string, iso?: string) => {
	return formatDate(dateFromIsoOrNow(iso), format)
}

/**
 * `Section.exerciseNames` is a JSON-encoded `string[]` written by the parser,
 * so rendering the column directly puts `["Squat"]` on the page. Parse it
 * defensively — the column is nullable, predates the current parser, and is
 * known to hold non-JSON in older rows.
 */
export function parseExerciseNames(raw: string | null | undefined): string[] {
	if (!raw) return []
	try {
		const names: unknown = JSON.parse(raw)
		if (!Array.isArray(names)) return []
		return names
			.filter((n): n is string => typeof n === 'string')
			.map((n) => n.trim())
			.filter(Boolean)
	} catch {
		return []
	}
}

/**
 * Human-readable exercise list for display, or null when there is nothing to
 * show — letting callers fall back to the raw notation.
 */
export function formatExerciseNames(
	raw: string | null | undefined,
): string | null {
	const names = parseExerciseNames(raw)
	return names.length > 0 ? names.join(', ') : null
}
